import { useMemo } from 'react';
import { useMyCompanyUserFor } from '@/companies/hooks';
import { isCompanyOwnerOrAdmin } from '@/companies/util/company-roles';
import { isTeamAdmin } from '@/companies/util/team-roles';

export type DevicePermissions = {
    /** Whether the viewer may edit any device in the company. */
    canEditDevices: boolean;
    /**
     * The teams a device may be assigned to: `null` means any team (the team
     * picker is paginated, so the full list is never in hand, and "any team" is
     * what owners/admins actually get). Otherwise the teams the viewer
     * administers — every other team is still listed, but disabled.
     */
    allowedTeamIds: string[] | null;
};

const defaultPermissions: DevicePermissions = {
    canEditDevices: false,
    allowedTeamIds: []
};

/**
 * What the viewer may do to the devices of a *given* company. Scoped by
 * `companyId` rather than read from the company context, because the standalone
 * device page — the QR-scan landing page — sits outside that context and its
 * device may belong to any company the viewer is in. Pass the selected
 * company's id on screens that are inside it.
 *
 * Fails closed: a viewer with no company user in that company gets nothing.
 */
export function useDevicePermissions(params: {
    companyId: string;
}): DevicePermissions {
    const { companyId } = params;

    const myCompanyUser = useMyCompanyUserFor(companyId);

    const canEditDevices = myCompanyUser
        ? isCompanyOwnerOrAdmin(myCompanyUser)
        : false;

    const allowedTeamIds = useMemo(() => {
        if (canEditDevices) {
            return null;
        }
        return (myCompanyUser?.teams ?? [])
            .filter(isTeamAdmin)
            .map((team) => team.teamId);
    }, [canEditDevices, myCompanyUser]);

    if (!myCompanyUser) {
        return defaultPermissions;
    }

    return {
        canEditDevices,
        allowedTeamIds
    };
}
